/**
 * プリセット一覧テーブル（Presentation）
 */
import type { FC } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Preset } from "../../../hooks/use-preset-api";

type PresetsTableProps = {
  presets: Preset[];
  onToggleActive: (presetId: string) => Promise<void>;
  onDelete: (presetId: string) => Promise<void>;
  isToggling: boolean;
  isDeleting: boolean;
};

const PRESET_TYPE_LABELS: Record<string, string> = {
  hr: "人材",
  matter: "案件",
};

/**
 * プリセット一覧テーブル
 * @param {PresetsTableProps} props コンポーネントProps
 * @return {JSX.Element} テーブル要素
 */
export const PresetsTable: FC<PresetsTableProps> = ({
  presets,
  onToggleActive,
  onDelete,
  isToggling,
  isDeleting,
}) => {
  const [actionId, setActionId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleToggleActive = async (presetId: string) => {
    setActionId(presetId);
    try {
      await onToggleActive(presetId);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (presetId: string) => {
    setActionId(presetId);
    try {
      await onDelete(presetId);
    } finally {
      setActionId(null);
      setDeleteConfirm(null);
    }
  };

  if (presets.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <p className="text-base-content/60">
            プリセットがまだありません。「+ 新規作成」から追加してください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card bg-base-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>名前</th>
                <th>種別</th>
                <th>スキル</th>
                <th>ステータス</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {presets.map(preset => (
                <tr key={preset.id}>
                  <td>
                    <Link
                      to={`/settings/presets/${preset.id}`}
                      className="font-medium hover:underline"
                    >
                      {preset.name}
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`badge badge-sm ${
                        preset.presetType === "hr"
                          ? "badge-info"
                          : "badge-secondary"
                      }`}
                    >
                      {PRESET_TYPE_LABELS[preset.presetType] ||
                        preset.presetType}
                    </span>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {preset.skills?.slice(0, 3).map(skill => (
                        <span
                          key={skill}
                          className="badge badge-outline badge-xs"
                        >
                          {skill}
                        </span>
                      ))}
                      {preset.skills && preset.skills.length > 3 && (
                        <span className="badge badge-ghost badge-xs">
                          +{preset.skills.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {preset.isActive ? (
                      <span className="badge badge-success badge-sm">有効</span>
                    ) : (
                      <span className="badge badge-ghost badge-sm">無効</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {preset.isActive ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => handleToggleActive(preset.id)}
                          disabled={isToggling && actionId === preset.id}
                        >
                          {isToggling && actionId === preset.id ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            "無効化"
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => handleToggleActive(preset.id)}
                          disabled={isToggling && actionId === preset.id}
                        >
                          {isToggling && actionId === preset.id ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            "有効化"
                          )}
                        </button>
                      )}
                      <Link
                        to={`/settings/presets/${preset.id}/edit`}
                        className="btn btn-ghost btn-xs"
                      >
                        編集
                      </Link>
                      {!preset.isActive && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs text-error"
                          onClick={() => setDeleteConfirm(preset.id)}
                          disabled={isDeleting && actionId === preset.id}
                        >
                          削除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">プリセットを削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting && actionId === deleteConfirm ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isDeleting && setDeleteConfirm(null)}
          />
        </div>
      )}
    </>
  );
};
